import { memo } from 'react'
import { Link } from 'react-router-dom'
import { Button } from "@/shared/components/ui/button"
import { Badge } from "@/shared/components/ui/badge"
import { Switch } from "@/shared/components/ui/switch"
import { ArrowLeft, Save, Play, Zap, Key, Building2, Check, Loader2 } from "lucide-react"
import { ProjectStatus } from "@/shared/types/enums"
import type { AgentProject } from "@/modules/agent/types/project"

interface ProjectToolbarProps {
  project: AgentProject
  projectId: string
  // 面板状态
  rightPanel: 'none' | 'config' | 'test' | 'skills'
  setRightPanel: (panel: 'none' | 'config' | 'test' | 'skills') => void
  // 记忆功能
  memoryEnabled: boolean
  isLoadingSettings: boolean
  isSavingSettings: boolean
  onMemoryToggle: (enabled: boolean) => void
  // 保存功能
  isSaved: boolean
  isSaving: boolean
  showSaveSuccess: boolean
  onSave: () => void
  // 发布功能
  isPublishing: boolean
  showPublishSuccess: boolean
  isPublished: boolean
  nodesCount: number
  onPublish: () => void
  // 测试功能
  onTest: () => void
  // 设置功能
  onOpenSettings: () => void
}

export const ProjectToolbar = memo(({
  project,
  projectId,
  rightPanel,
  setRightPanel,
  memoryEnabled,
  isLoadingSettings,
  isSavingSettings,
  onMemoryToggle,
  isSaved,
  isSaving,
  showSaveSuccess,
  onSave,
  isPublishing,
  showPublishSuccess,
  isPublished,
  nodesCount,
  onPublish,
  onTest,
  onOpenSettings
}: ProjectToolbarProps) => {
  
  return (
    <header className="h-16 border-b bg-white flex items-center justify-between px-6 shrink-0">
      {/* 左侧：项目信息 */}
      <div className="flex items-center gap-3">
        <Link to="/agent/dashboard">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{project.name}</span>
            <Badge 
              variant={
                project.status === ProjectStatus.ACTIVE ? 'default' : 
                project.status === ProjectStatus.MAINTENANCE ? 'secondary' : 
                'outline'
              } 
              className="text-xs"
            >
              {project.status === ProjectStatus.ACTIVE ? '运行中' : 
               project.status === ProjectStatus.MAINTENANCE ? '维护中' : 
               '草稿'}
            </Badge>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Building2 className="h-3 w-3" />
            <span>{project.company}</span>
            <span className="mx-1">•</span>
            <span>ID: {projectId}</span>
          </div>
        </div>
      </div>

      {/* 右侧：操作按钮 */}
      <div className="flex items-center gap-2">
        {/* API Key 管理按钮 */}
        <Button variant="outline" size="sm" className="gap-2" onClick={onOpenSettings}>
          <Key className="h-4 w-4" /> API Key
        </Button>
        
        {/* 测试运行按钮 */}
        <Button variant="outline" size="sm" className="gap-2" onClick={onTest}>
          <Play className="h-4 w-4" /> 测试运行
        </Button>

        {/* 技能配置按钮 */}
        <Button
          variant={rightPanel === 'skills' ? 'default' : 'outline'}
          size="sm"
          className="gap-2"
          onClick={() => setRightPanel(rightPanel === 'skills' ? 'none' : 'skills')}
        >
          <Zap className="h-4 w-4" /> 技能配置
        </Button>
        
        {/* 启用记忆开关 */}
        <div 
          className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-background"
          title="启用后，测试时将存储消息到 Memory，并触发实体提取和记忆召回"
        >
          <Switch
            id="memory-toggle"
            checked={memoryEnabled}
            onCheckedChange={onMemoryToggle}
            disabled={isLoadingSettings || isSavingSettings}
          />
          <label 
            htmlFor="memory-toggle"
            className={`text-sm cursor-pointer select-none ${
              memoryEnabled ? 'text-primary font-medium' : 'text-muted-foreground'
            }`}
          >
            {isSavingSettings ? '保存中...' : memoryEnabled ? '记忆已启用' : '启用记忆'}
          </label>
        </div>

        {/* 保存按钮 */}
        <Button 
          variant="outline" 
          size="sm" 
          className={`gap-2 ${showSaveSuccess ? 'text-green-600 border-green-600' : ''}`}
          onClick={onSave}
          disabled={isSaving}
        >
          {showSaveSuccess ? (
            <>
              <Check className="h-4 w-4" /> 已保存
            </>
          ) : isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> 保存中...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" /> 保存
              {!isSaved && <span className="w-2 h-2 rounded-full bg-amber-500 ml-1"></span>}
            </>
          )}
        </Button>

        {/* 发布按钮 */}
        <Button 
          size="sm" 
          className={`gap-2 ${showPublishSuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
          onClick={onPublish}
          disabled={isPublishing || !isSaved || nodesCount === 0}
          title={
            !isSaved ? '请先保存更改' : 
            nodesCount === 0 ? '请先创建流程' : 
            isPublished ? '当前版本已发布' : 
            '发布到运行时'
          }
        >
          {showPublishSuccess ? (
            <>
              <Check className="h-4 w-4" /> 已发布
            </>
          ) : isPublishing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> 发布中...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4" /> 发布
            </>
          )}
        </Button>
      </div>
    </header>
  )
})

ProjectToolbar.displayName = 'ProjectToolbar'